import { Icon } from "@opendash/icons";
import {
  Background,
  Controls,
  Edge,
  EdgeProps,
  Handle,
  Node,
  NodeProps,
  Position,
  ReactFlow,
  ReactFlowProvider,
  getSmoothStepPath,
} from "@xyflow/react";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import {
  SIV_DeliveryNotes,
  SIV_GoodsReceiptProcessPosition,
  SIV_GoodsReceivingItem,
  SIV_Package,
} from "../../../parsegenerated";

type EnrichmentEntry = {
  packages: SIV_Package[];
  deliveryNotes: SIV_DeliveryNotes[];
};

type Props = {
  items: SIV_GoodsReceivingItem[];
  enrichmentByPositionId: Map<string, EnrichmentEntry>;
};

// ─── Node types ───────────────────────────────────────────────────────────────

type FlowNodeData = {
  label: React.ReactNode;
  bg: string;
  borderColor: string;
  nodeWidth: number;
};

const handleStyle: React.CSSProperties = {
  width: 10,
  height: 10,
  background: "#ccc",
  border: "2px solid #fff",
};

const FlowNode: React.FC<NodeProps> = ({ data }) => {
  const d = data as FlowNodeData;
  const [hovered, setHovered] = useState(false);

  return (
    <>
      <Handle type="target" position={Position.Left} style={handleStyle} />
      <div
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          background: d.bg,
          border: `1px solid ${d.borderColor}`,
          borderRadius: 6,
          width: d.nodeWidth,
          fontSize: 12,
          cursor: "default",
          boxShadow: hovered
            ? `0 0 0 2px ${d.borderColor}, 0 4px 16px ${d.borderColor}88`
            : "0 1px 3px rgba(0,0,0,0.06)",
          transition: "box-shadow 0.18s ease",
        }}
      >
        {d.label as React.ReactNode}
      </div>
      <Handle type="source" position={Position.Right} style={handleStyle} />
    </>
  );
};

const HeaderNode: React.FC<NodeProps> = ({ data }) => (
  <>{(data as { label: React.ReactNode }).label}</>
);

// ─── Edge type ────────────────────────────────────────────────────────────────

type FlowEdgeData = { stroke: string };

const FlowEdge: React.FC<EdgeProps> = ({
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  animated,
  data,
}) => {
  const [hovered, setHovered] = useState(false);
  const [edgePath] = getSmoothStepPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
  });
  const stroke = ((data as FlowEdgeData | undefined)?.stroke) ?? "#bbb";

  return (
    <g onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      {/* Visible animated path */}
      <path
        d={edgePath}
        fill="none"
        className={
          animated
            ? "react-flow__edge-path react-flow__edge-animated"
            : "react-flow__edge-path"
        }
        style={{
          stroke,
          strokeWidth: hovered ? 4 : 2,
          filter: hovered ? `drop-shadow(0 0 5px ${stroke})` : "none",
          transition: "stroke-width 0.18s ease, filter 0.18s ease",
          pointerEvents: "none",
        }}
      />
      {/* Wide transparent area for hover detection */}
      <path
        d={edgePath}
        fill="none"
        strokeWidth={16}
        stroke="transparent"
        style={{ cursor: "default" }}
      />
    </g>
  );
};

// Defined outside the component so the reference stays stable
const nodeTypes = { flowNode: FlowNode, headerNode: HeaderNode };
const edgeTypes = { flowEdge: FlowEdge };

// ─── Layout constants ─────────────────────────────────────────────────────────

const STEP = 90;
const COL1_X = 0;
const COL2_X = 300;
const COL3_X = 580;
const HEADER_Y = -65;

// ─── Label components ─────────────────────────────────────────────────────────

const ItemLabel = ({ item }: { item: SIV_GoodsReceivingItem }) => (
  <div style={{ padding: "6px 10px", lineHeight: 1.4 }}>
    <div style={{ fontWeight: 600 }}>
      <Icon icon="fa:box-open" style={{ marginRight: 5, color: "#8c8c8c" }} />
      Pos. {item.itemNo}
    </div>
    {item.description && (
      <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>
        {item.description}
      </div>
    )}
    {item.amount != null && (
      <div style={{ fontSize: 11, color: "#aaa" }}>Menge: {item.amount}</div>
    )}
  </div>
);

const PosLabel = ({ pos }: { pos: SIV_GoodsReceiptProcessPosition }) => (
  <div style={{ padding: "6px 10px", lineHeight: 1.4 }}>
    <div style={{ fontWeight: 600 }}>
      <Icon icon="fa:truck" style={{ marginRight: 5, color: "#1677ff" }} />
      {pos.goodsReceiptProcess?.no ?? "—"}
    </div>
    <div style={{ fontSize: 11, color: "#555", marginTop: 2 }}>
      Pos. {pos.positionNo}
      {pos.orderNumber ? ` · ${pos.orderNumber}` : ""}
    </div>
    {pos.receivedQuantity != null && (
      <div style={{ fontSize: 11, color: "#aaa" }}>
        Empfangen: {pos.receivedQuantity}
      </div>
    )}
  </div>
);

const PkgLabel = ({ pkg }: { pkg: SIV_Package }) => (
  <div style={{ padding: "6px 10px", lineHeight: 1.4 }}>
    <div style={{ fontWeight: 600 }}>
      <Icon icon="fa:cube" style={{ marginRight: 5, color: "#722ed1" }} />
      {pkg.no}
    </div>
    {pkg.packagingType && (
      <div style={{ fontSize: 11, color: "#555", marginTop: 2 }}>
        {pkg.packagingType}
      </div>
    )}
    {pkg.packagingDate && (
      <div style={{ fontSize: 11, color: "#aaa" }}>
        {dayjs(pkg.packagingDate).format("DD.MM.YYYY")}
      </div>
    )}
  </div>
);

const DnLabel = ({ dn }: { dn: SIV_DeliveryNotes }) => (
  <div style={{ padding: "6px 10px", lineHeight: 1.4 }}>
    <div style={{ fontWeight: 600 }}>
      <Icon icon="fa:file-alt" style={{ marginRight: 5, color: "#389e0d" }} />
      {dn.no}
    </div>
    {dn.date && (
      <div style={{ fontSize: 11, color: "#aaa" }}>
        {dayjs(dn.date).format("DD.MM.YYYY")}
      </div>
    )}
    {dn.transportType && (
      <div style={{ fontSize: 11, color: "#555" }}>{dn.transportType}</div>
    )}
  </div>
);

const HeaderLabel = ({ text }: { text: string }) => (
  <div
    style={{
      padding: "3px 10px",
      fontSize: 11,
      fontWeight: 600,
      color: "#8c8c8c",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
    }}
  >
    {text}
  </div>
);

// ─── Flow data builder ────────────────────────────────────────────────────────

function buildFlowData(
  items: SIV_GoodsReceivingItem[],
  enrichmentByPositionId: Map<string, EnrichmentEntry>,
) {
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  if (items.length === 0) return { nodes, edges, height: 200 };

  const positionsSeen = new Map<string, SIV_GoodsReceiptProcessPosition>();
  items.forEach((item) => {
    const pos = item.goodsReceiptProcessPosition;
    if (pos?.id && !positionsSeen.has(pos.id)) positionsSeen.set(pos.id, pos);
  });

  const allPackages = new Map<string, SIV_Package>();
  const allDeliveryNotes = new Map<string, SIV_DeliveryNotes>();
  const posOrder = Array.from(positionsSeen.keys());

  posOrder.forEach((posId) => {
    enrichmentByPositionId.get(posId)?.packages.forEach((p) => allPackages.set(p.id!, p));
    enrichmentByPositionId.get(posId)?.deliveryNotes.forEach((d) => allDeliveryNotes.set(d.id!, d));
  });

  const itemY = new Map<string, number>();
  items.forEach((item, i) => itemY.set(item.id!, i * STEP));

  const posY = new Map<string, number>();
  positionsSeen.forEach((_, posId) => {
    const linked = items.filter((i) => i.goodsReceiptProcessPosition?.id === posId);
    const avg = linked.length
      ? linked.reduce((s, i) => s + (itemY.get(i.id!) ?? 0), 0) / linked.length
      : 0;
    posY.set(posId, avg);
  });

  let nextOutY = 0;
  const pkgY = new Map<string, number>();
  const dnY = new Map<string, number>();

  posOrder.forEach((posId) => {
    const before = pkgY.size + dnY.size;
    enrichmentByPositionId.get(posId)?.packages.forEach((pkg) => {
      if (!pkgY.has(pkg.id!)) { pkgY.set(pkg.id!, nextOutY); nextOutY += STEP; }
    });
    enrichmentByPositionId.get(posId)?.deliveryNotes.forEach((dn) => {
      if (!dnY.has(dn.id!)) { dnY.set(dn.id!, nextOutY); nextOutY += STEP; }
    });
    if (pkgY.size + dnY.size > before) nextOutY += 8;
  });

  const headerStyle: React.CSSProperties = { background: "transparent", border: "none", padding: 0, pointerEvents: "none" };
  const wrapStyle: React.CSSProperties = { padding: 0, background: "transparent", border: "none" };

  nodes.push(
    { id: "h-items", type: "headerNode", position: { x: COL1_X, y: HEADER_Y }, data: { label: <HeaderLabel text="Warenannahme-Pos." /> }, style: headerStyle, selectable: false },
    { id: "h-pos",   type: "headerNode", position: { x: COL2_X, y: HEADER_Y }, data: { label: <HeaderLabel text="WE-Positionen" /> },   style: headerStyle, selectable: false },
  );
  if (allPackages.size > 0 || allDeliveryNotes.size > 0) {
    nodes.push({ id: "h-out", type: "headerNode", position: { x: COL3_X, y: HEADER_Y }, data: { label: <HeaderLabel text="Packstücke / Lieferscheine" /> }, style: headerStyle, selectable: false });
  }

  items.forEach((item) => {
    nodes.push({
      id: `item-${item.id}`,
      type: "flowNode",
      position: { x: COL1_X, y: itemY.get(item.id!)! },
      data: { label: <ItemLabel item={item} />, bg: "#fafafa", borderColor: "#d9d9d9", nodeWidth: 210 } satisfies FlowNodeData,
      style: wrapStyle,
      selectable: false,
    });
  });

  positionsSeen.forEach((pos, posId) => {
    nodes.push({
      id: `pos-${posId}`,
      type: "flowNode",
      position: { x: COL2_X, y: posY.get(posId)! },
      data: { label: <PosLabel pos={pos} />, bg: "#e6f4ff", borderColor: "#91caff", nodeWidth: 185 } satisfies FlowNodeData,
      style: wrapStyle,
      selectable: false,
    });
  });

  pkgY.forEach((y, pkgId) => {
    const pkg = allPackages.get(pkgId);
    if (!pkg) return;
    nodes.push({
      id: `pkg-${pkgId}`,
      type: "flowNode",
      position: { x: COL3_X, y },
      data: { label: <PkgLabel pkg={pkg} />, bg: "#f9f0ff", borderColor: "#d3adf7", nodeWidth: 175 } satisfies FlowNodeData,
      style: wrapStyle,
      selectable: false,
    });
  });

  dnY.forEach((y, dnId) => {
    const dn = allDeliveryNotes.get(dnId);
    if (!dn) return;
    nodes.push({
      id: `dn-${dnId}`,
      type: "flowNode",
      position: { x: COL3_X, y },
      data: { label: <DnLabel dn={dn} />, bg: "#f6ffed", borderColor: "#b7eb8f", nodeWidth: 175 } satisfies FlowNodeData,
      style: wrapStyle,
      selectable: false,
    });
  });

  items.forEach((item) => {
    const pos = item.goodsReceiptProcessPosition;
    if (!pos?.id) return;
    edges.push({
      id: `e-item-${item.id}-pos-${pos.id}`,
      source: `item-${item.id}`,
      target: `pos-${pos.id}`,
      type: "flowEdge",
      animated: true,
      data: { stroke: "#91caff" } satisfies FlowEdgeData,
    });
  });

  posOrder.forEach((posId) => {
    enrichmentByPositionId.get(posId)?.packages.forEach((pkg) => {
      edges.push({
        id: `e-pos-${posId}-pkg-${pkg.id}`,
        source: `pos-${posId}`,
        target: `pkg-${pkg.id}`,
        type: "flowEdge",
        animated: true,
        data: { stroke: "#d3adf7" } satisfies FlowEdgeData,
      });
    });
    enrichmentByPositionId.get(posId)?.deliveryNotes.forEach((dn) => {
      edges.push({
        id: `e-pos-${posId}-dn-${dn.id}`,
        source: `pos-${posId}`,
        target: `dn-${dn.id}`,
        type: "flowEdge",
        animated: true,
        data: { stroke: "#b7eb8f" } satisfies FlowEdgeData,
      });
    });
  });

  const maxRows = Math.max(items.length, positionsSeen.size, pkgY.size + dnY.size);
  return { nodes, edges, height: Math.max(maxRows * STEP + 140, 250) };
}

// ─── Component ────────────────────────────────────────────────────────────────

export const GoodsReceivingFlowChart = ({ items, enrichmentByPositionId }: Props) => {
  const { nodes, edges, height } = useMemo(
    () => buildFlowData(items, enrichmentByPositionId),
    [items, enrichmentByPositionId],
  );

  if (items.length === 0) return null;

  return (
    <ReactFlowProvider>
      <div
        style={{
          width: "100%",
          height,
          border: "1px solid #f0f0f0",
          borderRadius: 8,
          overflow: "hidden",
          background: "#fafafa",
          cursor: "default",
        }}
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          panOnDrag={false}
          zoomOnScroll={false}
          preventScrolling={false}
          proOptions={{ hideAttribution: true }}
        >
          <Background color="#e8e8e8" gap={20} />
          <Controls showInteractive={false} position="bottom-right" />
        </ReactFlow>
      </div>
    </ReactFlowProvider>
  );
};
